import { useCallback, useEffect, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import { codeItemAdd, codeListGet, tagManagerPersist } from '../../api'
import { AttachmentView } from '../../common/attachments/attachmentview'
import { Button } from '../../common/Button.tsx'
import DefaultDatePicker from '../../common/datepicker/DefaultDatePicker.jsx'
import { IconAddNew } from '../../common/icons'
import { LoadingButton } from '../../common/loaders/loadingbutton'
import { PersonSelect2 } from '../../common/personselect2'
import { creatableSelectCodeSettings, creatableSelectTagSettings } from '../../common/reactselect'
import { fetchTags, sortByStringProp } from '../../common/utils'
import { actionTypeSelector, authSelector } from '../../reducers'

export function AddEquipment({ equipmentData, saveData, show, setShow }) {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)
	const actionType = useSelector(actionTypeSelector)
	const [data, setData] = useState(null)
	const prefix = 'equipment'
	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	// react-select
	const handleCodeItem = (newValue, actionMeta) =>
		setData({ ...data, [actionMeta.name]: { codeItemId: newValue.value } })

	const [equipmentCodes, setEquipmentCodes] = useState([])
	const [submitForm, setSubmitForm] = useState(false)

	const fetchEquipmentCodes = useCallback(async () => {
		const equipmentRes = await codeListGet('equipmentCodes')
		const benefitRes = await codeListGet('benefitCodes')
		const equipmentOptions = equipmentRes.data.items
			? equipmentRes.data.items.map((item) => ({
					codelistKey: equipmentRes.data.codeKey,
					value: item.codeItemId, // item.key
					label: item.codeValue, // item.description
				}))
			: []
		const benefitOptions = benefitRes.data.items
			? benefitRes.data.items.map((item) => ({
					codelistKey: benefitRes.data.codeKey,
					value: item.codeItemId, // item.key
					label: t(item.codeValue), // item.description
				}))
			: []
		const groupedOptions = [
			{
				label: t('EquipmentCodeItems', 'Välineet/käyttöoikeudet'),
				options: equipmentOptions.sort(sortByStringProp('label')),
			},
			{
				label: t('BenefitCodeItems', 'Luontoisedut'),
				options: benefitOptions.sort(sortByStringProp('label')),
			},
		]
		setEquipmentCodes(groupedOptions)
	}, [t])

	const handleCreateEquipmentCode = async (value) => {
		const res = await codeItemAdd('equipmentCodes', { codeValue: value })
		const { codeItemId, codeValue } = res.data
		const groupedOptions = [...equipmentCodes]
		groupedOptions[0].options = [...groupedOptions[0].options, { value: codeItemId, label: codeValue }]
		setEquipmentCodes(groupedOptions)
		setData({
			...data,
			equipmentCodeItem: { codeItemId: codeItemId },
		})
	}

	const [tagOptions, setTagOptions] = useState([])

	const handleTags = (options) =>
		setData({
			...data,
			tags: options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		})

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		const currentTags = data.tags ?? []

		setData({
			...data,
			tags: [...currentTags, { codeItemId: codeItemId, codeKey: codeKey }],
		})
	}

	// fetch code lists
	useEffect(() => {
		fetchEquipmentCodes()
		fetchTags(setTagOptions)
	}, [fetchEquipmentCodes])

	useEffect(() => {
		setData(
			show?.copy
				? {
						...equipmentData,
						id: 0,
						personId: null,
						orderDate: null,
						dueDate: null,
						deliveryDate: null,
						returningDate: null,
						returnDate: null,
						orderer: null,
						supplier: null,
						deliveredBy: null,
						returner: null,
						returnedTo: null,
					}
				: equipmentData,
		)
		if (!show) setSubmitForm(false)
	}, [show, equipmentData])

	const handleChange = ({ target }) => {
		const newData = { ...data }
		const value = target.type === 'checkbox' ? !target.checked : target.value // reversed checkbox for finished field

		newData[target.id] = value
		setData(newData)
	}

	const selectChange = (newValue, actionMeta) => {
		const selectedValue = newValue ? newValue.value : null
		setData({ ...data, [actionMeta.name]: selectedValue })
	}

	const handleSave = () => {
		setSubmitForm(true)
		saveData(data)
		//setShow(false);
	}

	const dispatch = useDispatch()

	const handleClose = () => setShow(false)
	const handleShow = () => {
		// empty fields when creating new
		dispatch({
			type: 'CLEAR_EQUIPMENT',
			payload: { equipment: null },
		})
		setData({
			id: 0,
			personId: '',
			personNumber: '',
			personName: '',
			description: '',
			additionalInformation: '',
			code: '',
			accessKey: '',
			yearModel: '',
			otherIdentifier: '',
			valuePayroll: '',
			valueOther: '',
			orderDate: '',
			orderer: '',
			dueDate: '',
			supplier: '',
			deliveryDate: '',
			deliveredBy: '',
			returningDate: '',
			returner: '',
			returnDate: '',
			returnedTo: '',
		})
		setShow(true)
	}

	let modalTitle
	if (data?.id > 0) {
		const desc = data?.equipmentCodeItem?.description || data?.equipmentCodeItem?.codeValue
		const code = data?.code
		if (desc && code) {
			modalTitle = `${desc} - ${code}`
		} else if (desc) {
			modalTitle = desc
		} else if (code) {
			modalTitle = `${t('EquipmentOrAccessRight', 'Välineistö/käyttöoikeus')} - ${code}`
		} else {
			modalTitle = t('EquipmentOrAccessRight', 'Välineistö/käyttöoikeus')
		}
	} else {
		modalTitle = t('NewEquipmentOrAccessRight', 'Uusi välineistö tai käyttöoikeus')
	}

	return (
		<>
			{!isPersonal && (
				<Button testId="add-equipment" className="equipmentbtn addbutton" onClick={handleShow} variant="primary">
					<IconAddNew /> {t('AddEquipmentOrAccessRight', 'Lisää välineistö/käyttöoikeus')}
				</Button>
			)}
			{show && (
				<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
					<Modal.Header closeButton>
						<Modal.Title>{modalTitle}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Row className="mb-1">
							<Form.Group as={Col} controlId="personId">
								<PersonSelect2
									label={t('Person', 'Henkilö')}
									field="personId"
									data={data}
									selectChange={selectChange}
									supervised={auth.role === 'SUPERVISOR'}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>
						<Row className="mb-1">
							<Form.Group as={Col} controlId="equipmentCodeItem">
								<Form.Label>{t('EquipmentCodeItem', 'Väline/käyttöoikeus')}</Form.Label>
								<CreatableSelect
									name="equipmentCodeItem"
									inputId="equipmentCodeItem"
									value={
										equipmentCodes
											.flatMap((group) => group.options)
											.find((option) => option.value === data?.equipmentCodeItem?.codeItemId) || null
									} // find from both groups
									onChange={handleCodeItem}
									onCreateOption={handleCreateEquipmentCode}
									options={equipmentCodes}
									{...creatableSelectCodeSettings(t)}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} controlId="description">
								<Form.Label>{t('Description', 'Kuvaus')}</Form.Label>
								<Form.Control as="textarea" rows="4" value={data?.description ?? ''} onChange={handleChange} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} controlId="additionalInformation">
								<Form.Label>{t('AdditionalInformation', 'Lisätiedot')}</Form.Label>
								<Form.Control
									as="textarea"
									rows="4"
									value={data?.additionalInformation ?? ''}
									onChange={handleChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} controlId="code">
								<Form.Label>{t('CodeNumberIdentifier', 'Tunnus/numero/tunniste')}</Form.Label>
								<Form.Control onChange={handleChange} value={data?.code ?? ''} />
							</Form.Group>
							<Form.Group as={Col} xl={6} controlId="accessKey">
								<Form.Label>{t('AccessKeyPinPassword', 'Pääsyavain/PIN/Salasana')}</Form.Label>
								<Form.Control onChange={handleChange} value={data?.accessKey ?? ''} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} controlId="yearModel">
								<Form.Label>{t('YearModel', 'Vuosimalli')}</Form.Label>
								<Form.Control onChange={handleChange} value={data?.yearModel ?? ''} />
							</Form.Group>
							<Form.Group as={Col} xl={6} controlId="otherIdentifier">
								<Form.Label>{t('OtherIdentifier', 'Muu tunniste')}</Form.Label>
								<Form.Control onChange={handleChange} value={data?.otherIdentifier ?? ''} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} controlId="valuePayroll">
								<Form.Label>{t('ValuePayroll', 'Arvo/summa (palkanlaskentaan)')}</Form.Label>
								<Form.Control
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									type="number"
									onChange={handleChange}
									value={data?.valuePayroll ?? ''}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6} controlId="valueOther">
								<Form.Label>{t('ValueOther', 'Muu arvo/summa')}</Form.Label>
								<Form.Control
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									type="number"
									onChange={handleChange}
									value={data?.valueOther ?? ''}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} className="mt-4" controlId="orderDate">
								<DefaultDatePicker
									id={`${prefix}-orderDate`}
									name="orderDate"
									label={t('OrderDate', 'Tilauspvm.')}
									value={data?.orderDate ?? ''}
									onChange={(iso) => handleChange({ target: { id: 'orderDate', value: iso ?? '' } })}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6}>
								<PersonSelect2
									label={t('Orderer', 'Tilaaja')}
									field="orderer"
									data={data}
									selectChange={selectChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} className="mt-4" controlId="dueDate">
								<DefaultDatePicker
									id={`${prefix}-dueDate`}
									name="dueDate"
									label={t('DueDate', 'Eräpvm.')}
									value={data?.dueDate ?? ''}
									onChange={(iso) => handleChange({ target: { id: 'dueDate', value: iso ?? '' } })}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6}>
								<PersonSelect2
									label={t('Supplier', 'Eräpäivä/vastuuhenkilö')}
									field="supplier"
									data={data}
									selectChange={selectChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} className="mt-4" controlId="deliveryDate">
								<DefaultDatePicker
									id={`${prefix}-deliveryDate`}
									name="deliveryDate"
									label={t('DeliveryDate', 'Luovutus pvm.')}
									value={data?.deliveryDate ?? ''}
									onChange={(iso) => handleChange({ target: { id: 'deliveryDate', value: iso ?? '' } })}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6}>
								<PersonSelect2
									label={t('DeliveredBy', 'Luovuttanut henkilö')}
									field="deliveredBy"
									data={data}
									selectChange={selectChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} className="mt-4" controlId="returningDate">
								<DefaultDatePicker
									id={`${prefix}-returningDate`}
									name="returningDate"
									label={t('ReturnedBy', 'Palautettava/suljettava viimeistään')}
									value={data?.returningDate ?? ''}
									onChange={(iso) => handleChange({ target: { id: 'returningDate', value: iso ?? '' } })}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6}>
								<PersonSelect2
									label={t('Returner', 'Palauttaja tai sulkija/vastuuhenkilö')}
									field="returner"
									data={data}
									selectChange={selectChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} className="mt-4" controlId="returnDate">
								<DefaultDatePicker
									id={`${prefix}-returnDate`}
									name="returnDate"
									label={t('ReturnDate', 'Palautus pvm.')}
									value={data?.returnDate ?? ''}
									onChange={(iso) => handleChange({ target: { id: 'returnDate', value: iso ?? '' } })}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6}>
								<PersonSelect2
									label={t('ReturnedTo', 'Palautettu henkilölle')}
									field="returnedTo"
									data={data}
									selectChange={selectChange}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-3">
							<Form.Group as={Col} controlId="equipmentkeywords">
								<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
								<CreatableSelect
									inputId="equipmentkeywords"
									value={data?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
									onChange={handleTags}
									onCreateOption={handleCreateTag}
									options={tagOptions}
									{...creatableSelectTagSettings(t)}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-3">
							<Form.Group as={Col} controlId="attachments">
								<h4>{t('Attachments', 'Liitteet')}</h4>
							</Form.Group>
						</Row>
						<AttachmentView
							section="equipment"
							entityid={data?.id}
							attachments={data?.attachments}
							savedId={
								(actionType === 'POST_EQUIPMENT_SUCCEEDED' || actionType === 'POST_EQUIPMENT_EDIT_SUCCEEDED') &&
								data?.id
							}
						/>
					</Modal.Body>
					<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button testId="cancel" variant="cancel" onClick={handleClose}>
							{t('Cancel', 'Keskeytä')}
						</Button>
						<Button testId="save" variant="primary" onClick={handleSave} disabled={submitForm || data?.personId < 1}>
							{submitForm && <LoadingButton />}
							{t('Save', 'Tallenna')}
						</Button>
					</Modal.Footer>
				</Modal>
			)}
		</>
	)
}
